import * as React from 'react';
import { observer } from 'mobx-react';
import { Validator } from 'class-validator';

import { DataTableView, ToolbarVisibility, Column } from 'src/core/ui/data-table.view';
import { AutocompleteView } from 'src/core/ui/autocomplete/autocomplete.view';
import { TextView } from 'src/core/ui/text.view';
import { DropdownView } from 'src/core/ui/dropdown.view';
import { CardController } from 'src/app/shared/card.controller';

interface ContractsAgeBandViewProps extends ComponentViewProps<DataSet<AgeBandItem>> {
	mode: ControlMode;
	columns: any[];
	updateAgeBands: (items: AgeBandItem[], oldType?: string, newType?: string) => void;
	ageTypesQuery: DropdownRequest<AutocompleteSummary>;
}

interface IOption {
	label: string;
	value: number;
	minAge: number;
	maxAge: number;
}

@observer
export class ContractsAgeBandView extends React.Component<ContractsAgeBandViewProps, any> {
	private options: IOption[] = [
		{
			label: 'Adult',
			value: 0,
			minAge: 13,
			maxAge: 99,
		},
		{
			label: 'Child',
			value: 1,
			minAge: 3,
			maxAge: 12,
		},
		{
			label: 'Infant',
			value: 2,
			minAge: 0,
			maxAge: 2,
		}
	];

	private columns: Column[] = [
		{
			key: 'type',
			title: 'Type',
			dataIndex: 'type',
			editable: (text: number, record: AgeBandItem, index: number, isSelected: boolean) => {
				if (isSelected) {
					return (
						<DropdownView
							required={true}
							disabled={this.props.mode == 'display'}
							label={null}
							model={record.type}
							options={this.options}
							onChanged={newVal => record.type = newVal ? newVal.value : undefined}
						/>
					);
				}
				const option = this.options.find(o => o.value == text);
				return option ? option.label : text;
			}
		},
		{
			key: 'label',
			title: 'Label',
			dataIndex: 'label',
			editable: (text: string, record: AgeBandItem, index: number, isSelected: boolean) => {
				if (isSelected) {
					return (
						<AutocompleteView
							required={true}
							disabled={this.props.mode == 'display'}
							label={null}
							model={record.label}
							request={this.props.ageTypesQuery}
							onChanged={newVal => {
								if (newVal != null) {
									const items = [record];
									this.props.updateAgeBands(items, record.label ? record.label : record.type.toString(), newVal.value);
									record.label = newVal.value;
								}
							}}
						/>
					);
				}
				return text != null ? text : "";
			}
		},
		{
			key: 'minAge',
			title: 'Min Age',
			dataIndex: 'minAge',
			editable: (text: string, record: AgeBandItem, index: number, isSelected: boolean) => {
				if (isSelected) {
					return <TextView mode='edit'
						label={null}
						value={record.minAge}
						onChanged={newVal => {
							record.minAge = newVal;
						}}
					/>;
				}
				return text != null ? text : "";
			}
		},
		{
			key: 'maxAge',
			title: 'Max Age',
			dataIndex: 'maxAge',
			editable: (text: string, record: AgeBandItem, index: number, isSelected: boolean) => {
				if (isSelected) {
					return <TextView mode='edit'
						label={null}
						value={record.maxAge}
						onChanged={newVal => {
							record.maxAge = newVal;
						}}
					/>;
				}
				return text != null ? text : "";
			}
		}
	];

	private validator: Validator;

	public constructor(props) {
		super(props);
		this.fillColumns();
		this.validator = new Validator();
	}

	public componentWillReceiveProps(nextProps: Readonly<ContractsAgeBandViewProps>, nextContext: any) {
		this.fillColumns();
	}

	public render() {
		if (this.props.model == null) {
			return null;
		}

		return (
			<CardController
				icon="team"
				title="Age Bands"
				mode={this.props.mode}
				className="card-borderless"
			>
				<DataTableView
					rowKey={(record: AgeBandItem) => `${record.type}-${record.minAge}-${record.maxAge}`}
					mode={this.props.mode}
					setBusy={this.props.setBusy}
					columns={this.columns}
					isValidItem={this.isValidAgeBand}
					model={this.props.model}
					createNewItem={this.newAge}
					afterDeleteItem={this.deleteAge}
					hideToolbar={ToolbarVisibility.hideSearch | ToolbarVisibility.hidePagination | ToolbarVisibility.hideFooter}
				/>
			</CardController>
		);
	}

	private newAge = async () => {
		const opts = this.options.filter(o => this.props.model.items.every(e => o.value != e.type));
		const opt = opts.length > 0 ? opts[0] : this.options[0];
		const res = await this.props.model.create(() => ({
			type: opt.value,
			label: opt.label,
			minAge: opt.minAge,
			maxAge: opt.maxAge,
		}));
		this.fillColumns();
		const items = [res];
		this.props.updateAgeBands(items);
		return res;
	}

	private deleteAge = (items: AgeBandItem[]) => {
		this.fillColumns();
		this.props.updateAgeBands(items);
	}

	private fillColumns() {
		const types = this.props.model.items.map(o => o.type);
		this.props.columns.splice(0, this.props.columns.length);
		for (const type of types) {
			if (this.props.columns.indexOf(type) < 0)
				this.props.columns.push(type);
		}
	}

	private isValidAgeBand = async (item: AgeBandItem) => {
		const errors = [];
		if (this.validator.isEmpty(item.type)) {
			errors.push('type is required');
		}
		if (this.validator.isEmpty(item.minAge)) {
			errors.push('min age is required');
		}
		if (this.validator.isEmpty(item.maxAge)) {
			errors.push('max age is required');
		}
		if (!this.validator.isNumberString(item.minAge.toString())) {
			errors.push('min age must be a number');
		}
		if (!this.validator.isNumberString(item.maxAge.toString())) {
			errors.push('max age  must be a number');
		}
		return errors;
	}
}



// WEBPACK FOOTER //
// ./src/app/products/contract/form/parts/contract-ageband.view.tsx